import type { WsEvent } from '@fixflow/shared-types';

export type TicketDraftRepair = {
  deviceIndex: number;
  serviceId: string;
  serviceName: string;
  quotedPrice: number;
};

export type TicketDraftDevice = {
  label: string;
};

function buildDraftDeviceGroups(
  devices: TicketDraftDevice[],
  repairs: TicketDraftRepair[],
): { label: string; services: { id: string; name: string; price: string }[] }[] {
  const deviceCount = Math.max(devices.length, 1, ...repairs.map((r) => r.deviceIndex + 1));
  const groups: { label: string; services: { id: string; name: string; price: string }[] }[] = [];
  for (let i = 0; i < deviceCount; i++) {
    groups.push({
      label: devices[i]?.label?.trim() || `Device ${i + 1}`,
      services: [],
    });
  }
  for (const r of repairs) {
    const idx = Math.min(Math.max(r.deviceIndex, 0), groups.length - 1);
    groups[idx]!.services.push({
      id: `${r.deviceIndex}-${r.serviceId}`,
      name: r.serviceName,
      price: String(r.quotedPrice),
    });
  }
  if (deviceCount <= 1) return groups;
  return groups.filter((g) => g.services.length > 0);
}

export function buildTicketDraftDisplayEvent(input: {
  storeName: string;
  logoUrl?: string | null;
  customerName: string | null;
  devices: TicketDraftDevice[];
  repairs: TicketDraftRepair[];
}): WsEvent {
  const { storeName, logoUrl, customerName, devices, repairs } = input;
  const deviceSummaries = devices.map((d) => d.label).filter(Boolean);
  const deviceGroups = buildDraftDeviceGroups(devices, repairs);
  const services = deviceGroups.flatMap((g) => g.services);
  const subtotal = repairs.reduce((sum, r) => sum + r.quotedPrice, 0);
  const totalStr = subtotal.toFixed(2);

  return {
    type: 'display.update',
    payload: {
      mode: 'review',
      isTicketDraft: true,
      ticketNumber: null,
      storeName,
      logoUrl: logoUrl ?? null,
      customerName: customerName?.trim() || null,
      devices: deviceSummaries,
      deviceGroups,
      services,
      items: [],
      subtotal: totalStr,
      discountTotal: '0.00',
      taxTotal: '0.00',
      total: totalStr,
      balanceDue: totalStr,
    },
    timestamp: new Date().toISOString(),
  };
}

export function hasTicketDraftContent(input: {
  customerName: string | null;
  devices: TicketDraftDevice[];
  repairs: TicketDraftRepair[];
}): boolean {
  if (input.customerName?.trim()) return true;
  if (input.repairs.length > 0) return true;
  return input.devices.some((d) => d.label && !/^Device \d+$/i.test(d.label));
}
